/**
 * Drawing Annotations Geometry & Metrics.
 *
 * Geometric coordinate and dimension calculations for price range measurement brackets
 * and interactive text badge labels.
 */

export interface Point2D {
  x: number;
  y: number;
}

export interface ChartPoint {
  time: number;
  price: number;
}

export interface PriceRangeGeometry {
  range: number;
  rangeText: string;
  midX: number;
  topY: number;
  bottomY: number;
  midY: number;
  bracketArmWidth: number;
  labelX: number;
  labelY: number;
}

export interface TextBadgeGeometry {
  boxX: number;
  boxY: number;
  width: number;
  height: number;
  padding: number;
  textX: number;
  textY: number;
}

/**
 * Computes rendering coordinates and bracket dimensions for a Price Range drawing.
 */
export function computePriceRangeGeometry(
  p1: ChartPoint | null | undefined,
  p2: ChartPoint | null | undefined,
  coordToPixel: (time: number, price: number) => Point2D | null,
  bracketArmWidth: number = 8,
): PriceRangeGeometry | null {
  if (!p1 || !p2 || coordToPixel == null) return null;

  const p1Px = coordToPixel(p1.time, p1.price);
  const p2Px = coordToPixel(p2.time, p2.price);
  if (!p1Px || !p2Px) return null;

  const midX = (p1Px.x + p2Px.x) / 2;
  const topY = Math.min(p1Px.y, p2Px.y);
  const bottomY = Math.max(p1Px.y, p2Px.y);
  const midY = (topY + bottomY) / 2;
  const range = Math.abs(p1.price - p2.price);

  return {
    range,
    rangeText: range.toFixed(2),
    midX,
    topY,
    bottomY,
    midY,
    bracketArmWidth,
    labelX: midX + 12,
    labelY: midY,
  };
}

/**
 * Computes layout box coordinates and baseline positions for a Text drawing badge.
 */
export function computeTextBadgeGeometry(
  p1: ChartPoint | null | undefined,
  text: string = "",
  fontSize: number = 14,
  padding: number = 6,
  coordToPixel: (time: number, price: number) => Point2D | null,
  textWidthEstimator?: (text: string, fontSize: number) => number,
): TextBadgeGeometry | null {
  if (!p1 || coordToPixel == null) return null;

  const pPx = coordToPixel(p1.time, p1.price);
  if (!pPx) return null;

  const cleanText = text || "";
  const textWidth = textWidthEstimator
    ? textWidthEstimator(cleanText, fontSize)
    : cleanText.length * 8;

  const width = textWidth + padding * 2;
  const height = fontSize + padding * 2;

  return {
    boxX: pPx.x,
    boxY: pPx.y - height,
    width,
    height,
    padding,
    textX: pPx.x + padding,
    textY: pPx.y - height / 2,
  };
}
